
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bot, Clock, Mail, Phone, Calendar, AlertCircle } from "lucide-react";
import { useAutomationRules } from "./hooks/useAutomationRules";
import { AutomationRuleEditor } from "./AutomationRuleEditor";
import { FollowUpSequenceManager } from "./FollowUpSequenceManager";
import { TaskOrchestrator } from "./TaskOrchestrator";
import { AutomationErrorBoundary } from "./AutomationErrorBoundary";
import { LoadingState } from "@/components/error-handling/LoadingState";

export const TaskAutomationEngine: React.FC = () => {
  const { rules, isLoading, createRule, updateRule, deleteRule } = useAutomationRules();
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <AutomationErrorBoundary>
      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <CardTitle>Task Automation Engine</CardTitle>
            </div>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              Active
            </Badge>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="rules">Automation Rules</TabsTrigger>
                <TabsTrigger value="sequences">Follow-up Sequences</TabsTrigger>
                <TabsTrigger value="orchestration">Task Orchestration</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4">
                {isLoading ? (
                  <LoadingState message="Loading automation overview..." />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Active Rules</p>
                            <p className="text-2xl font-bold">{rules.filter(r => r.enabled).length}</p>
                          </div>
                          <AlertCircle className="h-8 w-8 text-blue-500" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Tasks Generated Today</p>
                            <p className="text-2xl font-bold">47</p>
                          </div>
                          <Clock className="h-8 w-8 text-green-500" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Automation Rate</p>
                            <p className="text-2xl font-bold">73%</p>
                          </div>
                          <Bot className="h-8 w-8 text-purple-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="rules">
                <AutomationRuleEditor 
                  rules={rules}
                  onCreateRule={createRule}
                  onUpdateRule={updateRule}
                  onDeleteRule={deleteRule}
                  isLoading={isLoading}
                />
              </TabsContent>
              
              <TabsContent value="sequences">
                <AutomationErrorBoundary>
                  <FollowUpSequenceManager />
                </AutomationErrorBoundary>
              </TabsContent>
              
              <TabsContent value="orchestration">
                <AutomationErrorBoundary>
                  <TaskOrchestrator />
                </AutomationErrorBoundary>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AutomationErrorBoundary>
  );
};
